import { StylesConfig } from "react-select";
import { LAYERS } from "Components/DesignSystem/layers";

/**
 * The Recategorization react-select skin, on role tokens.
 *
 * These four `Select`s are the only controls in the flow that paint from JS rather
 * than from a class, so they were the one place `data-theme` could not reach: a
 * white menu with `#172B4D` rows and a `#0747a6` selection, baked at module scope.
 *
 * `var(--ds-*)` resolves in an inline style exactly as it does in a stylesheet, so
 * the values follow the theme with no JS listener — the same trick `useChartTheme`
 * needs an observer for only because ApexCharts reads colours into a canvas.
 *
 * Every fallback is the LIGHT value of its role, so a stylesheet that failed to load
 * still renders today's control rather than a transparent one.
 *
 * Replaced wholesale when these move to DS `Combobox`; until then this is the seam.
 */
const ROLE = {
    edge: 'var(--ds-edge, #dadfe8)',
    accent: 'var(--ds-accent, #030562)',
    surface: 'var(--ds-surface, #ffffff)',
    surfaceHover: 'var(--ds-surface-hover, #f6f7fa)',
    ink: 'var(--ds-ink, #001233)',
    inkInverse: 'var(--ds-ink-inverse, #ffffff)',
} as const;

export const customStyles: StylesConfig<OptionType, false> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: ROLE.surface,
        border: `1px solid ${state.isFocused ? ROLE.accent : ROLE.edge}`,
        boxShadow: state.isFocused ? `0 0 0 1px ${ROLE.accent}` : 'none',
        '&:hover': {
            border: `1px solid ${state.isFocused ? ROLE.accent : ROLE.edge}`
        }
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? ROLE.accent : state.isFocused ? ROLE.surfaceHover : ROLE.surface,
        color: state.isSelected ? ROLE.inkInverse : ROLE.ink,
        '&:hover': {
            backgroundColor: state.isSelected ? ROLE.accent : ROLE.surfaceHover
        }
    }),
    /* The closed control's own text — it inherited `color` from react-select's
       defaults, which is a fixed near-black. */
    singleValue: (provided) => ({ ...provided, color: ROLE.ink }),
    input: (provided) => ({ ...provided, color: ROLE.ink }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: ROLE.surface,
        zIndex: 9999
    }),
    /**
     * The open list is PORTALLED to `document.body`, and this is the style that makes
     * that land correctly.
     *
     * Every one of these selects now sits inside a DS `Table`, whose wrapper is
     * `tw-overflow-x-auto`. A non-`visible` overflow on one axis clips the other too,
     * so an `menuPosition: "absolute"` menu is trapped inside the table box: the
     * wrapper scrolls to reveal it and the row the user was working in slides out of
     * sight. Reported 2026-09-13 on the coilVoltage column.
     *
     * `LAYERS.overlay` rather than a literal: these lists open from inside the legacy
     * `#modal-root` (z-5000), so the number has to clear it — which is exactly what
     * § 283 calibrated the overlay rung to do. A bare `zIndex: 9999` would also clear
     * it today and silently stop doing so the day the ladder moves again.
     */
    menuPortal: (provided) => ({
        ...provided,
        zIndex: LAYERS.overlay
    })
};
